import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Space, Typography, Alert, Tooltip, Select } from 'antd';
import {
  CameraOutlined,
  RedoOutlined,
  CheckOutlined,
  SwapOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

export interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  title?: string;
}

export const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({
  open,
  onClose,
  onCapture,
  title = 'Tomar Foto para el Log',
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const videoRef = useRef<HTMLVideoElement>(null);
  const nativeFileInputRef = useRef<HTMLInputElement>(null);

  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  }, [stream]);

  const startCamera = useCallback(
    async (deviceId?: string, preferredFacing: 'environment' | 'user' = facingMode) => {
      setErrorMsg(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMsg('Tu navegador o dispositivo no admite acceso directo a la cámara web. Puedes usar la cámara nativa del sistema.');
        return;
      }

      // Detener cualquier stream anterior
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      try {
        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId } }
            : {
                facingMode: { ideal: preferredFacing },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
          audio: false,
        };

        let newStream: MediaStream;
        try {
          newStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (initialErr: any) {
          // Si el usuario denegó permisos o no hay dispositivo, relanzar directamente
          if (
            initialErr?.name === 'NotAllowedError' ||
            initialErr?.name === 'PermissionDeniedError' ||
            initialErr?.name === 'NotFoundError' ||
            initialErr?.name === 'DevicesNotFoundError'
          ) {
            throw initialErr;
          }
          // Reintento con configuración básica solo si fallaron las restricciones avanzadas
          newStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }

        setStream(newStream);

        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          await videoRef.current.play().catch(() => {});
        }

        // Listar cámaras disponibles si no se han cargado
        const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setVideoDevices(videoInputs);
        if (!deviceId && videoInputs.length > 0) {
          const currentTrack = newStream.getVideoTracks()[0];
          const activeDeviceId = currentTrack?.getSettings()?.deviceId;
          if (activeDeviceId) {
            setSelectedDeviceId(activeDeviceId);
          }
        }
      } catch (err: any) {
        let msg = 'No se pudo acceder a la cámara.';
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          msg = 'Permiso denegado para acceder a la cámara. Concede el permiso en tu navegador o usa la cámara del dispositivo.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          msg = 'No se encontró ninguna cámara disponible en el dispositivo.';
        }
        setErrorMsg(msg);
      }
    },
    [facingMode, stream]
  );

  // Iniciar la cámara al abrir el modal si no hay foto capturada
  useEffect(() => {
    if (open && !capturedPreview) {
      void startCamera(selectedDeviceId || undefined, facingMode);
    }

    return () => {
      stopStream();
    };
  }, [open]);

  // Si cambia el deviceId seleccionado manualmente
  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    void startCamera(deviceId, facingMode);
  };

  // Alternar entre frontal y trasera (útil en móviles y tablets)
  const handleToggleFacingMode = () => {
    const nextFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(nextFacing);
    setSelectedDeviceId(null);
    void startCamera(undefined, nextFacing);
  };

  // Tomar captura del cuadro de video actual
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const previewUrl = URL.createObjectURL(blob);
        setCapturedPreview(previewUrl);
        setCapturedBlob(blob);
        stopStream();
      },
      'image/jpeg',
      0.9
    );
  };

  // Descartar foto capturada y volver a la cámara en vivo
  const handleRetake = () => {
    if (capturedPreview) {
      URL.revokeObjectURL(capturedPreview);
    }
    setCapturedPreview(null);
    setCapturedBlob(null);
    void startCamera(selectedDeviceId || undefined, facingMode);
  };

  // Confirmar y entregar el archivo File resultante
  const handleConfirmPhoto = () => {
    if (!capturedBlob) return;

    const fileName = `foto_ciclo_${dayjs().format('YYYYMMDD_HHmmss')}.jpg`;
    const file = new File([capturedBlob], fileName, { type: 'image/jpeg' });

    if (capturedPreview) {
      URL.revokeObjectURL(capturedPreview);
    }
    setCapturedPreview(null);
    setCapturedBlob(null);
    stopStream();

    onCapture(file);
  };

  // Salir y limpiar
  const handleModalClose = () => {
    if (capturedPreview) {
      URL.revokeObjectURL(capturedPreview);
    }
    setCapturedPreview(null);
    setCapturedBlob(null);
    stopStream();
    onClose();
  };

  // Manejar captura directa vía input de archivo nativo (cámara nativa móvil)
  const handleNativeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleModalClose();
      onCapture(file);
    }
    e.target.value = '';
  };

  return (
    <Modal
      title={
        <Space>
          <CameraOutlined style={{ color: '#1890ff' }} />
          <span>{title}</span>
        </Space>
      }
      open={open}
      onCancel={handleModalClose}
      footer={null}
      destroyOnHidden
      width={560}
      centered
      styles={{ body: { padding: '16px' } }}
    >
      {/* Input oculto para abrir la cámara nativa en móviles / tablets o como fallback */}
      <input
        ref={nativeFileInputRef}
        data-testid="native-camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleNativeFileChange}
      />

      {errorMsg ? (
        <div style={{ textAlign: 'center', padding: '16px 8px' }}>
          <Alert
            message="Acceso a la cámara"
            description={errorMsg}
            type="warning"
            showIcon
            style={{ marginBottom: 16, textAlign: 'left' }}
          />
          <Button
            type="primary"
            icon={<MobileOutlined />}
            size="large"
            onClick={() => nativeFileInputRef.current?.click()}
          >
            Usar cámara del dispositivo
          </Button>
        </div>
      ) : capturedPreview ? (
        // Vista de confirmación de la foto capturada
        <div>
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxHeight: '400px',
              backgroundColor: '#000',
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={capturedPreview}
              alt="Foto capturada"
              style={{
                width: '100%',
                maxHeight: '400px',
                objectFit: 'contain',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 16,
              gap: 8,
            }}
          >
            <Button icon={<RedoOutlined />} onClick={handleRetake}>
              Repetir foto
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleConfirmPhoto}
              style={{ fontWeight: 600 }}
            >
              Usar esta foto
            </Button>
          </div>
        </div>
      ) : (
        // Vista de transmisión en vivo de la cámara
        <div>
          {videoDevices.length > 1 && (
            <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cámara:
              </Text>
              <Select
                size="small"
                value={selectedDeviceId || undefined}
                style={{ flex: 1 }}
                onChange={handleDeviceChange}
                options={videoDevices.map((d, index) => ({
                  value: d.deviceId,
                  label: d.label || `Cámara ${index + 1}`,
                }))}
              />
              <Tooltip title="Cambiar orientación de cámara">
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={handleToggleFacingMode}
                />
              </Tooltip>
            </div>
          )}

          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '320px',
              backgroundColor: '#1f1f1f',
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />

            {/* Guía visual sutil para encuadre */}
            <div
              style={{
                position: 'absolute',
                top: 20,
                bottom: 20,
                left: 20,
                right: 20,
                border: '1.5px dashed rgba(255, 255, 255, 0.45)',
                borderRadius: 8,
                pointerEvents: 'none',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 16,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Button
              type="link"
              size="small"
              icon={<MobileOutlined />}
              onClick={() => nativeFileInputRef.current?.click()}
              style={{ padding: 0 }}
            >
              Cámara nativa del teléfono
            </Button>

            <Space>
              <Button onClick={handleModalClose}>Cancelar</Button>
              <Button
                type="primary"
                icon={<CameraOutlined />}
                onClick={handleCapturePhoto}
                style={{ fontWeight: 600 }}
              >
                Capturar foto
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CameraCaptureModal;

